import { Injectable } from '@angular/core';
import { Observable, catchError, map, of, switchMap, tap } from 'rxjs';

import { IPaymentDetails } from '../../../../interfaces/payment.interface';
import { Payment } from '../../domain/model/payment.model';
import { trackSubscriptions } from '../../../../../shared/operators/subscription-tracker.operator';
import { ManualPaymentService } from '../../domain/services/manual-payment.service';
import { PaymentEventsService } from '../services/payment-events.service';
import { PaymentTrackerService } from '../services/payment-tracker.service';
import { PaymentContextService } from '../services/payment-context.service';
import { PaymentTicketService } from '../services/payment-ticket.service';
import {Logger} from "../../../../interfaces/Logger";
import { PaymentContext } from '../../domain/value-objects/payment-context.model';
import { IPaymentResult } from '../../domain/interfaces/payment-storage.interface';

@Injectable({ providedIn: 'root' })
export class ProcessManualPaymentUseCase {
  constructor(
    private readonly logger: Logger,
    private readonly paymentContext: PaymentContextService,
    private readonly paymentTracker: PaymentTrackerService,
    private readonly manualPaymentService: ManualPaymentService,
    private readonly paymentEvents: PaymentEventsService,
    private readonly paymentTicket: PaymentTicketService
  ) {}

  execute(paymentDetails: IPaymentDetails): Observable<IPaymentResult> {
    return this.paymentContext.getContext().pipe(
      tap(context => this.paymentTracker.trackPaymentInitiation(context, paymentDetails)),
      switchMap(context => this.processPayment(context, paymentDetails)),
      switchMap(({ payment, context }) => this.processTicket(context, payment, paymentDetails)),
      trackSubscriptions('[ProcessManualPaymentUseCase] execute', this.logger),
      catchError(error => this.handleError(error))
    );
  }

  private processPayment(context: PaymentContext, paymentDetails: IPaymentDetails): Observable<{ payment: Payment; context: PaymentContext }> {
    return this.manualPaymentService.processPayment(context, paymentDetails).pipe(
      tap(payment => this.paymentTracker.trackPaymentCompletion(payment, paymentDetails)),
      map(payment => ({ payment, context }))
    );
  }

  private processTicket(context: PaymentContext, payment: Payment, paymentDetails: IPaymentDetails): Observable<IPaymentResult> {
    return this.paymentTicket.generateTicket(paymentDetails, context.account, payment).pipe(
      tap(() => this.paymentTracker.trackTicketEventUpdate(context.ticketEvent?._id)),
      tap(ticket => this.paymentEvents.notifyPaymentCompletion({
        context,
        payment,
        paymentDetails,
        ticket
      })),
      map(ticket => ({
        success: true,
        payment,
        ticket
      }))
    );
  }

  private handleError(error: Error): Observable<IPaymentResult> {
    this.logger.error(error, 'Manual payment processing failed');

    return of({
      success: false,
      message: 'Manual payment processing failed',
      payment: null,
      ticket: null
    });
  }
}
